import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import api, { cachedGet, readCache } from "../api";
import BottomNavBar from "../landingPageNew/BottomNavBar";
import HHLogo from "../sidebar/logo.svg";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import "react-quill/dist/quill.bubble.css";

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 0 0 90px 0;
  min-height: 100vh;
  background: ${(props) => (props.isDarkMode ? "#262c3c" : "#FCF5E5")};
`;

const MessageCard = styled.div`
  background: ${(props) => (props.isDarkMode ? "#262c3c" : "#FCF5E5")};
  border-radius: 0;
  box-shadow: none;
  margin: 0;
  overflow: hidden;
`;

const MessageHeader = styled.div`
  text-align: center;
  padding: 32px 24px 24px 24px;
  background: linear-gradient(135deg, #8747d4 0%, #a855f7 100%);
`;

const Icon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
  font-family: "Quicksand", sans-serif;
  line-height: 1.3;
`;

const Subtitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
`;

const Logo = styled.img`
  width: 40px;
  height: 40px;
  background-color: #222222;
  border-radius: 8px;
  padding: 6px;
`;

const SubtitleText = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-family: "Quicksand", sans-serif;
`;

const DateText = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin: 8px 0 0 0;
  font-family: "Quicksand", sans-serif;
`;

const MessageBody = styled.div`
  padding: 24px;
  font-size: 15px;
  line-height: 1.5;
  color: ${(props) => (props.isDarkMode ? "#e0e0e0" : "#333")};
  font-family: "Quicksand", sans-serif;

  /* Quill content styling */
  .ql-editor {
    padding: 0;
    font-family: "Quicksand", sans-serif;
  }

  p {
    margin-bottom: 10px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 24px;
    margin-bottom: 12px;
    font-weight: 700;
    line-height: 1.3;
  }

  h1 {
    font-size: 32px;
  }

  h2 {
    font-size: 24px;
  }

  h3 {
    font-size: 20px;
  }

  strong {
    font-weight: 700;
  }

  em {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }

  s {
    text-decoration: line-through;
  }

  ul,
  ol {
    margin: 16px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  a {
    color: #8747d4;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  blockquote {
    border-left: 4px solid #8747d4;
    padding-left: 16px;
    margin: 16px 0;
    font-style: italic;
    color: ${(props) => (props.isDarkMode ? "#bbb" : "#666")};
  }

  code {
    background: ${(props) => (props.isDarkMode ? "#2a2a2a" : "#f5f5f5")};
    padding: 2px 6px;
    border-radius: 4px;
    font-family: "Courier New", monospace;
    font-size: 14px;
  }

  pre {
    background: ${(props) => (props.isDarkMode ? "#2a2a2a" : "#f5f5f5")};
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
  }

  pre code {
    background: none;
    padding: 0;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 16px 0;
  }
`;

const MessageFooter = styled.div`
  padding: 20px 24px;
  text-align: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-family: "Quicksand", sans-serif;
  line-height: 1.6;
  background: linear-gradient(135deg, #8747d4 0%, #a855f7 100%);
`;

const FooterLink = styled.a`
  color: #fff;
  text-decoration: underline;
  font-weight: 600;

  &:hover {
    opacity: 0.8;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 100px 20px;
  font-size: 16px;
  color: ${(props) => (props.isDarkMode ? "#aaa" : "#999")};
  font-family: "Quicksand", sans-serif;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 100px 20px;
  font-size: 16px;
  color: #d14343;
  font-family: "Quicksand", sans-serif;
`;

const ReplySection = styled.div`
  padding: 24px;
  border-top: 1px solid ${(props) => (props.isDarkMode ? "#3a3a4a" : "#e0e0e0")};
`;

const ReplyLabel = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.isDarkMode ? "#ccc" : "#555")};
  margin: 0 0 10px 0;
  font-family: "Quicksand", sans-serif;
`;

const ReplyTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px 14px;
  border: 2px solid ${(props) => (props.isDarkMode ? "#444" : "#e0e0e0")};
  border-radius: 10px;
  font-size: 14px;
  font-family: "Quicksand", sans-serif;
  background: ${(props) => (props.isDarkMode ? "#1a1a2e" : "#fff")};
  color: ${(props) => (props.isDarkMode ? "#e0e0e0" : "#333")};
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #8747d4;
  }

  &::placeholder {
    color: ${(props) => (props.isDarkMode ? "#666" : "#aaa")};
  }
`;

const ReplyButton = styled.button`
  margin-top: 10px;
  background: linear-gradient(135deg, #8747d4 0%, #a855f7 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  font-family: "Quicksand", sans-serif;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(135, 71, 212, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ReplyStatus = styled.p`
  font-size: 13px;
  margin: 8px 0 0 0;
  font-family: "Quicksand", sans-serif;
  font-weight: 600;
  color: ${(props) => (props.error ? "#d14343" : "#2f8f2f")};
`;

const SalaamDetail = () => {
  const { isDarkMode } = useAmazonContext();
  const { id } = useParams();
  const userEmail = localStorage.getItem("userEmail");
  // Opened from the list, so the announcements are already cached and this renders at once.
  const cachedAnnouncement = (
    readCache("/api/announcements", { email: userEmail }, 30000)?.data || []
  ).find((a) => a._id === id);
  const [announcement, setAnnouncement] = useState(cachedAnnouncement || null);
  const [isLoading, setIsLoading] = useState(!cachedAnnouncement);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyStatus, setReplyStatus] = useState(null);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      if (!userEmail || !id) {
        setError("Invalid request");
        setIsLoading(false);
        return;
      }

      try {
        const announcements = await cachedGet("/api/announcements", {
          params: { email: userEmail },
          ttl: 30000,
        });
        const found = (announcements || []).find((a) => a._id === id);

        if (!found) {
          setError("Announcement not found");
        } else {
          setAnnouncement(found);
          
          // Mark as read
          const isRead = (found.readBy || []).includes(userEmail);
          if (!isRead) {
            try {
              await api.post(`/api/announcements/${id}/mark-read`, {
                email: userEmail,
              });
            } catch (err) {
              console.error("Failed to mark as read:", err);
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch announcement:", err);
        setError("Failed to load announcement");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnnouncement();
  }, [id, userEmail]);

  const handleReplySubmit = async () => {
    if (!replyText.trim()) return;
    setReplySubmitting(true);
    setReplyStatus(null);
    try {
      await api.post(`/api/announcements/${id}/reply`, {
        email: userEmail,
        message: replyText.trim(),
      });
      setReplyText("");
      setReplyStatus({ type: "success", text: "We have received your reply! Thank you for your message." });
    } catch (err) {
      console.error("Failed to send reply:", err);
      setReplyStatus({ type: "error", text: "Failed to send reply. Please try again." });
    } finally {
      setReplySubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <Container isDarkMode={isDarkMode}>
          <LoadingState isDarkMode={isDarkMode}>Loading...</LoadingState>
        </Container>
        <BottomNavBar />
      </>
    );
  }

  if (error || !announcement) {
    return (
      <>
        <Container isDarkMode={isDarkMode}>
          <ErrorState>{error || "Announcement not found"}</ErrorState>
        </Container>
        <BottomNavBar />
      </>
    );
  }

  return (
    <>
      <Container isDarkMode={isDarkMode}>
        <MessageCard isDarkMode={isDarkMode}>
          <MessageHeader isDarkMode={isDarkMode}>
            <Icon>{announcement.icon || "📢"}</Icon>
            <Title isDarkMode={isDarkMode}>{announcement.title}</Title>
            <Subtitle>
              <Logo src={HHLogo} alt="HH" />
              <SubtitleText isDarkMode={isDarkMode}>
                A message from HH
              </SubtitleText>
            </Subtitle>
            {announcement.createdAt && (
              <DateText isDarkMode={isDarkMode}>
                {new Date(announcement.createdAt).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </DateText>
            )}
          </MessageHeader>

          <MessageBody
            isDarkMode={isDarkMode}
            dangerouslySetInnerHTML={{ __html: announcement.message }}
          />

          {announcement.allowReplies && (
            <ReplySection isDarkMode={isDarkMode}>
              <ReplyLabel isDarkMode={isDarkMode}>Reply to this message</ReplyLabel>
              <ReplyTextArea
                isDarkMode={isDarkMode}
                placeholder="Write your reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                disabled={replySubmitting}
              />
              <ReplyButton
                onClick={handleReplySubmit}
                disabled={replySubmitting || !replyText.trim()}
              >
                {replySubmitting ? "Sending..." : "Send Reply"}
              </ReplyButton>
              {replyStatus && (
                <ReplyStatus error={replyStatus.type === "error"}>
                  {replyStatus.text}
                </ReplyStatus>
              )}
            </ReplySection>
          )}

          <MessageFooter isDarkMode={isDarkMode}>
            If you have any questions, please email us at{" "}
            <FooterLink href="mailto:hereforthehereafter@gmail.com">
              hereforthehereafter@gmail.com
            </FooterLink>
          </MessageFooter>
        </MessageCard>
      </Container>
      <BottomNavBar />
    </>
  );
};

export default SalaamDetail;

